import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { getLandingPreviewData } from '../utils/landingEditor';

export default function LandingEmpresaPublica() {
  const { slug } = useParams();
  const { usuario } = useAuth();
  const [empresa, setEmpresa] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [modalAbierto, setModalAbierto] = useState(false);
  const [form, setForm] = useState({ nombre: '', email: '', telefono: '', empresa: '', notas: '' });
  const [enviando, setEnviando] = useState(false);
  const [estado, setEstado] = useState({ tipo: '', mensaje: '' });
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth <= 760 : false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 760);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let activo = true;
    api.get(`/empresas/publico/${encodeURIComponent(slug || '')}`)
      .then(({ data }) => {
        if (activo) {
          setEmpresa(data);
          setForm((prev) => ({ ...prev, empresa: data?.nombre || prev.empresa || '' }));
        }
      })
      .catch(() => {
        if (activo) setError('Esta página no está disponible o no ha sido activada todavía.');
      })
      .finally(() => {
        if (activo) setCargando(false);
      });
    return () => { activo = false; };
  }, [slug]);

  const landing = empresa?.landingPage || {};
  const preview = useMemo(() => getLandingPreviewData(landing, empresa || {}), [landing, empresa]);

  const abrirModal = () => {
    setEstado({ tipo: '', mensaje: '' });
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setEstado({ tipo: '', mensaje: '' });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setEnviando(true);
    setEstado({ tipo: '', mensaje: '' });

    try {
      const payload = {
        ...form,
        empresaId: empresa?._id || '',
        empresa: form.empresa || empresa?.nombre || '',
        origen: 'landing-empresa',
        empresaSlug: slug || '',
      };

      const { data } = await api.post('/empresas/publico/registro', payload);
      setEstado({ tipo: 'ok', mensaje: data?.mensaje || 'Gracias. Hemos recibido tu solicitud y te contactaremos pronto.' });
      setForm({ nombre: '', email: '', telefono: '', empresa: empresa?.nombre || '', notas: '' });
      setTimeout(() => {
        cerrarModal();
      }, 900);
    } catch (err) {
      setEstado({
        tipo: 'error',
        mensaje: err?.response?.data?.error || 'No hemos podido enviar la solicitud. Inténtalo de nuevo en unos minutos.',
      });
    } finally {
      setEnviando(false);
    }
  };

  if (cargando) {
    return <div style={{ padding: 40, textAlign: 'center' }}>Cargando landing page…</div>;
  }

  if (error || !empresa) {
    return <div style={{ padding: 40, textAlign: 'center' }}>{error || 'No se encontró la empresa.'}</div>;
  }

  const servicios = Array.isArray(preview?.servicios) ? preview.servicios : [];
  const beneficios = Array.isArray(preview?.beneficios) ? preview.beneficios : [];
  const stats = Array.isArray(preview?.stats) ? preview.stats : [];
  const testimonials = Array.isArray(preview?.testimonials) ? preview.testimonials : [];
  const bloques = Array.isArray(preview?.bloques) ? preview.bloques : [];
  const htmlLibre = preview?.htmlLibre || '';
  const colorPrimario = preview?.colorPrimario || '#4f46e5';
  const colorSecundario = preview?.colorSecundario || '#0ea5e9';
  const logoCabecera = preview?.logo || empresa.logo || '';
  const heroImage = preview?.heroImage || '';
  const layoutVariant = preview?.layoutVariant || 'clasico';
  const showStats = preview?.showStats !== false;
  const showTestimonials = preview?.showTestimonials !== false;
  const puedeEditarLanding = ['admin', 'superadmin'].includes(usuario?.rol);

  const heroStyle = {
    background: heroImage
      ? `linear-gradient(135deg, rgba(15,23,42,0.82) 0%, ${colorPrimario}cc 38%, ${colorSecundario}d9 100%), url(${heroImage}) center/cover no-repeat`
      : `linear-gradient(135deg, #0f172a 0%, ${colorPrimario} 42%, ${colorSecundario} 100%)`,
    color: '#fff',
    padding: layoutVariant === 'minimalista' ? '56px 24px 76px' : '60px 24px 86px',
    borderRadius: 0,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: 'inset 0 -1px 0 rgba(255,255,255,0.1)',
  };

  const cardStyle = {
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    borderRadius: 24,
    padding: 24,
    boxShadow: layoutVariant === 'moderno' ? '0 20px 40px rgba(15,23,42,0.08)' : '0 12px 30px rgba(15,23,42,0.04)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', color: '#0f172a' }}>
      {modalAbierto && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.56)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20 }}>
          <div style={{ width: '100%', maxWidth: 560, background: '#fff', borderRadius: 22, boxShadow: '0 26px 64px rgba(15, 23, 42, 0.26)', padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 800, color: colorPrimario }}>Contacto</div>
                <h3 style={{ margin: '8px 0 0', fontSize: 28, color: '#0f172a' }}>{preview.ctaTexto || 'Solicitar demo'}</h3>
              </div>
              <button type="button" aria-label="Cerrar modal" onClick={cerrarModal} style={{ border: 'none', width: 36, height: 36, borderRadius: 999, background: '#f1f5f9', color: '#0f172a', fontSize: 22, cursor: 'pointer' }}>×</button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 14 }}>
              <label style={{ display: 'grid', gap: 8 }}>
                <span style={{ fontWeight: 700, color: '#0f172a' }}>Nombre *</span>
                <input type="text" required name="nombre" value={form.nombre} onChange={handleChange} placeholder="Tu nombre" style={{ border: '1px solid #dfe7f5', borderRadius: 10, padding: '12px 14px', fontSize: 15 }} />
              </label>

              <label style={{ display: 'grid', gap: 8 }}>
                <span style={{ fontWeight: 700, color: '#0f172a' }}>Email *</span>
                <input type="email" required name="email" value={form.email} onChange={handleChange} placeholder="tuemail@empresa.com" style={{ border: '1px solid #dfe7f5', borderRadius: 10, padding: '12px 14px', fontSize: 15 }} />
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
                <label style={{ display: 'grid', gap: 8 }}>
                  <span style={{ fontWeight: 700, color: '#0f172a' }}>Teléfono</span>
                  <input type="tel" name="telefono" value={form.telefono} onChange={handleChange} placeholder="600 123 456" style={{ border: '1px solid #dfe7f5', borderRadius: 10, padding: '12px 14px', fontSize: 15 }} />
                </label>

                <label style={{ display: 'grid', gap: 8 }}>
                  <span style={{ fontWeight: 700, color: '#0f172a' }}>Empresa</span>
                  <input type="text" name="empresa" value={form.empresa} onChange={handleChange} placeholder={empresa?.nombre || 'Nombre de tu empresa'} style={{ border: '1px solid #dfe7f5', borderRadius: 10, padding: '12px 14px', fontSize: 15 }} />
                </label>
              </div>

              <label style={{ display: 'grid', gap: 8 }}>
                <span style={{ fontWeight: 700, color: '#0f172a' }}>Notas</span>
                <textarea name="notas" value={form.notas} onChange={handleChange} rows={4} placeholder="Cuéntanos qué necesitas o qué problema quieres resolver" style={{ border: '1px solid #dfe7f5', borderRadius: 10, padding: '12px 14px', fontSize: 15, resize: 'vertical' }} />
              </label>

              {estado.mensaje ? (
                <div style={{ borderRadius: 10, padding: '10px 12px', fontSize: 14, background: estado.tipo === 'ok' ? '#ecfdf5' : '#fef2f2', color: estado.tipo === 'ok' ? '#166534' : '#991b1b', border: `1px solid ${estado.tipo === 'ok' ? '#bbf7d0' : '#fecaca'}` }}>
                  {estado.mensaje}
                </div>
              ) : null}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 6 }}>
                <button type="button" onClick={cerrarModal} style={{ border: '1px solid #cbd5e1', background: '#fff', color: '#0f172a', borderRadius: 10, padding: '11px 16px', fontWeight: 700, cursor: 'pointer' }}>
                  Cancelar
                </button>
                <button type="submit" disabled={enviando} style={{ border: 'none', background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', color: '#fff', borderRadius: 10, padding: '11px 18px', fontWeight: 800, cursor: 'pointer', opacity: enviando ? 0.8 : 1 }}>
                  {enviando ? 'Enviando…' : 'Enviar solicitud'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div style={{ position: 'fixed', top: 14, left: 18, zIndex: 1100 }}>
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: 'rgba(15,23,42,0.68)', backdropFilter: 'blur(10px)', color: '#fff', textDecoration: 'none', padding: '8px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, boxShadow: '0 10px 28px rgba(15,23,42,0.18)', border: '1px solid rgba(255,255,255,0.12)' }}>
          <span style={{ width: 18, height: 18, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#fff', color: colorPrimario, fontWeight: 900, fontSize: 11 }}>T</span>
          TRIX
        </Link>
      </div>
      {puedeEditarLanding ? (
        <div style={{ position: 'fixed', top: 18, right: 18, zIndex: 1000 }}>
          <Link to={{ pathname: '/app/empresas', search: `?tab=landing&empresaId=${encodeURIComponent(empresa._id || '')}` }} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#0f172a', color: '#fff', textDecoration: 'none', padding: '10px 14px', borderRadius: 999, boxShadow: '0 8px 24px rgba(15,23,42,0.18)', fontWeight: 700 }}>
            ✏️ Editar landing
          </Link>
        </div>
      ) : null}
      <header style={heroStyle}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            {logoCabecera ? (
              <img
                src={logoCabecera}
                alt={empresa.nombre}
                style={{ width: 110, height: 110, borderRadius: 22, objectFit: 'cover', background: '#fff', boxShadow: '0 18px 42px rgba(15,23,42,0.18)', border: '1px solid rgba(255,255,255,0.35)' }}
              />
            ) : null}
            <div>
              <div style={{ fontSize: 13, opacity: 0.8, letterSpacing: '0.14em', textTransform: 'uppercase' }}>{empresa.nombre}</div>
              <strong style={{ fontSize: 30, lineHeight: 1.2, display: 'block', letterSpacing: '-0.03em' }}>{preview.titulo || 'Gestión digital para tu negocio'}</strong>
            </div>
          </div>
          <button type="button" onClick={abrirModal} style={{ background: '#fff', color: colorPrimario, padding: '12px 20px', borderRadius: 999, fontWeight: 700, boxShadow: '0 16px 28px rgba(15,23,42,0.15)', border: 'none', cursor: 'pointer' }}>{preview.ctaTexto || 'Solicitar demo'}</button>
        </div>

        <div style={{ maxWidth: 1100, margin: '38px auto 0', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.2fr 0.8fr', gap: 34, alignItems: 'stretch' }}>
          <div style={{ minWidth: 0 }}>
            {preview.heroBadge ? <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.14)', borderRadius: 999, padding: '9px 14px', marginBottom: 18, fontSize: 13, fontWeight: 700, border: '1px solid rgba(255,255,255,0.15)' }}>{preview.heroBadge}</div> : null}
            {preview.slogan ? <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.08)', borderRadius: 999, padding: '8px 12px', marginBottom: 12, color: 'rgba(255,255,255,0.92)', border: '1px solid rgba(255,255,255,0.1)' }}>{preview.slogan}</div> : null}
            <h1 style={{ margin: '0 0 16px', fontSize: 'clamp(2.8rem, 5vw, 4.6rem)', lineHeight: 1.06, letterSpacing: '-0.05em' }}>{preview.titulo || 'Gestión digital para tu negocio'}</h1>
            <p style={{ margin: 0, fontSize: 18, lineHeight: 1.7, maxWidth: 640, opacity: 0.96 }}>{preview.descripcion || 'Trix centraliza clientes, ventas, tareas, facturación y operaciones en un único espacio.'}</p>
            <div style={{ marginTop: 28, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button type="button" onClick={abrirModal} style={{ background: '#fff', color: colorPrimario, borderRadius: 14, padding: '14px 18px', fontWeight: 700, boxShadow: '0 20px 36px rgba(15,23,42,0.14)', border: 'none', cursor: 'pointer' }}>{preview.ctaTexto || 'Solicitar demo'}</button>
              {preview.whatsapp ? <a href={`https://wa.me/${preview.whatsapp.replace(/\s+/g, '')}`} target="_blank" rel="noreferrer" style={{ border: '1px solid rgba(255,255,255,0.4)', color: '#fff', borderRadius: 14, padding: '14px 18px', textDecoration: 'none', fontWeight: 700 }}>WhatsApp</a> : null}
            </div>
          </div>

          <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: 28, padding: 24, backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.16)', boxShadow: '0 22px 44px rgba(15,23,42,0.18)', minWidth: 0, overflow: 'hidden' }}>
            <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.12em', opacity: 0.8, marginBottom: 12 }}>Resumen</div>
            {heroImage ? (
              <div style={{ marginBottom: 12 }}>
                <img
                  src={heroImage}
                  alt={`Cabecera de ${empresa.nombre}`}
                  style={{ width: '100%', height: 128, objectFit: 'cover', borderRadius: 18, border: '1px solid rgba(255,255,255,0.24)' }}
                />
              </div>
            ) : null}
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 14, padding: 16, border: '1px solid rgba(255,255,255,0.08)' }}><strong>{empresa.tipoNegocio || 'Negocio'}</strong><div style={{ opacity: 0.8, marginTop: 6 }}>Sector</div></div>
              <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 14, padding: 16, border: '1px solid rgba(255,255,255,0.08)' }}><strong>{preview.heroBadge || 'Atención personalizada'}</strong><div style={{ opacity: 0.8, marginTop: 6 }}>Enfoque</div></div>
              {preview.email ? <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 14, padding: 16, border: '1px solid rgba(255,255,255,0.08)' }}><strong>{preview.email}</strong><div style={{ opacity: 0.8, marginTop: 6 }}>Contacto</div></div> : null}
            </div>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '56px 24px 80px' }}>
        {servicios.length > 0 && (
          <section style={{ marginBottom: 52 }}>
            <div style={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
              <h2 style={{ fontSize: 32, margin: 0 }}>Qué incluye</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18 }}>
              {servicios.map((item, index) => (
                <div key={`${item}-${index}`} style={{ ...cardStyle, cursor: 'default' }}>
                  <div style={{ width: 42, height: 42, borderRadius: 14, display: 'grid', placeItems: 'center', background: `${colorPrimario}15`, color: colorPrimario, fontWeight: 800, marginBottom: 12 }}>✓</div>
                  <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 17 }}>{item}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {beneficios.length > 0 && (
          <section style={{ marginBottom: 52 }}>
            <h2 style={{ fontSize: 32, margin: '0 0 20px' }}>Beneficios clave</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
              {beneficios.map((item, index) => (
                <div key={`${item}-${index}`} style={{ ...cardStyle, background: '#f8fafc' }}>
                  <div style={{ color: colorPrimario, fontWeight: 800, marginBottom: 8, fontSize: 18 }}>{index + 1}</div>
                  <div style={{ color: '#334155', lineHeight: 1.7 }}>{item}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {showStats && stats.length > 0 && (
          <section style={{ marginBottom: 48 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
              {stats.map((item, index) => (
                <div key={`${item}-${index}`} style={{ ...cardStyle, textAlign: 'center', padding: 18 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: colorPrimario }}>{item}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {showTestimonials && testimonials.length > 0 && (
          <section style={{ marginBottom: 48 }}>
            <h2 style={{ fontSize: 32, margin: '0 0 20px' }}>Lo que dicen</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
              {testimonials.map((item, index) => (
                <div key={`${item}-${index}`} style={{ ...cardStyle, background: '#f8fafc' }}>
                  <div style={{ fontSize: 16, lineHeight: 1.7, color: '#334155' }}>{item}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {htmlLibre ? (
          <section style={{ marginBottom: 32 }} dangerouslySetInnerHTML={{ __html: htmlLibre }} />
        ) : null}

        {bloques.filter((b) => b?.activo !== false).length > 0 && (
          <section style={{ display: 'grid', gap: 18, marginBottom: 32 }}>
            {bloques.filter((b) => b?.activo !== false).sort((a, b) => (a.orden || 0) - (b.orden || 0)).map((bloque) => (
              <div key={bloque.id || bloque.titulo || Math.random()} style={{ ...cardStyle, borderRadius: 20 }}>
                {bloque.titulo ? <h3 style={{ marginTop: 0, marginBottom: 12 }}>{bloque.titulo}</h3> : null}
                {bloque.tipo === 'iframe' && bloque.contenido ? (
                  <iframe
                    src={bloque.contenido}
                    title={bloque.titulo || 'Contenido embebido'}
                    style={{ width: '100%', border: 'none', borderRadius: 12, minHeight: bloque.altura || 280 }}
                    loading="lazy"
                  />
                ) : null}
                {bloque.tipo === 'html' && bloque.contenido ? (
                  <div dangerouslySetInnerHTML={{ __html: bloque.contenido }} />
                ) : null}
                {bloque.tipo === 'cta' && bloque.contenido ? (
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <a href={bloque.contenido} style={{ background: colorPrimario, color: '#fff', borderRadius: 12, padding: '12px 16px', textDecoration: 'none', fontWeight: 700 }}>
                      {bloque.titulo || 'Ver más'}
                    </a>
                  </div>
                ) : null}
                {bloque.tipo === 'texto' && bloque.contenido ? (
                  <p style={{ margin: 0, color: '#475569', lineHeight: 1.7 }}>{bloque.contenido}</p>
                ) : null}
              </div>
            ))}
          </section>
        )}


      </main>
    </div>
  );
}
